/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { CheckState } from '../../enum/check-state.enum';
import { Selectable } from '../../interface/selectable';
import { IComparator } from '../../interface/icomparator';
import { CommonUtils } from '../../tool/common-utils';
import * as uuid from 'uuid';

/**
 * Represent an item used to create a checkbox item inside a comboBox
 */
export class ComboItem<T extends IComparator> implements Selectable {
  public id = uuid.v4();

  public get checkState(): CheckState {
    if (this.items) {
      const count = this.items.filter((item) => item.check).length;
      switch (count) {
        case 0:
          return CheckState.uncheck;
        case this.items.length:
          return CheckState.check;
        default:
          return CheckState.partial;
      }
    } else {
      return this.check ? CheckState.check : CheckState.uncheck;
    }
  }

  public get check(): boolean {
    if (this.items) {
      return (
        this.items.filter((item) => item.check).length === this.items.length
      );
    } else {
      return this._check;
    }
  }

  public set check(value: boolean) {
    if (this.items) {
      this.items.forEach((item) => (item.check = value));
    } else {
      this._check = value;
    }
  }

  public icon: string;
  /**
   * Representable item from datasource
   */
  public item: T;
  /**
   * Checked state o item
   */
  public preselect: boolean;

  private _check: boolean;

  public enable = true;
  public iconColor: string;

  items?: ComboItem<IComparator>[];
  normalizedText: string;
  public selection = 'none';

  public constructor(item: T, check?: boolean, items?: IComparator[]) {
    this.item = item;
    this.normalizedText = CommonUtils.normalize(item.toString(), true);
    this.check = check;
    if (items) {
      this.items = items.map((c) => this.buildItem(c, check));
    }
    if (Object.hasOwn(item, 'icon')) {
      this.icon = item['icon'];
    }
    if (Object.hasOwn(item, 'iconColor')) {
      this.iconColor = item['iconColor'];
    }
  }

  public addItem(item: IComparator) {
    this.items.push(new ComboItem<IComparator>(item));
  }

  compare(item: any): number {
    return this.item.compare(item);
  }

  public toString(): string {
    return this.item.toString() + '';
  }

  getCheckValues(): ComboItem<any>[] {
    const items = [];
    if (this.items) {
      for (const item of this.items) {
        if (item.check) {
          items.push(item);
        }
      }
    } else if (this.check) {
      items.push(this);
    }
    return items;
  }

  checkByCompare(v: any): ComboItem<any> {
    if (this.items) {
      const item = this.items.find((child) => child.checkByCompare(v));
      if (item) {
        item.check = true;
        return item;
      }
    } else if (this.item && this.item.compare) {
      if (this.item.compare(v) === 0) {
        this.check = true;
        return this;
      }
    }
    return null;
  }

  private buildItem(c: IComparator, check: boolean): ComboItem<IComparator> {
    const x = c as any;
    const itemCheckValue = x.check;
    const checkValue = check ? check : itemCheckValue;
    return new ComboItem(c, checkValue);
  }
}
